import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { LiveQuizSettingsConfig } from '../../../types/form';

interface MobileQuizSettingsProps {
  settings: LiveQuizSettingsConfig;
  onChange: (settings: LiveQuizSettingsConfig) => void;
  onBack: () => void;
}

type Section = 'timing' | 'scoring' | 'display' | 'gameplay';

// Define sections array explicitly for type-safe iteration
const SECTIONS: readonly Section[] = ['timing', 'scoring', 'display', 'gameplay'] as const;

const sectionInfo: Record<Section, { label: string; icon: React.ReactNode; color: string }> = {
  timing: {
    label: 'Timing',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    color: 'blue',
  },
  scoring: {
    label: 'Scoring',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
    color: 'orange',
  },
  display: {
    label: 'Display',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    ),
    color: 'purple',
  },
  gameplay: {
    label: 'Gameplay',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    color: 'green',
  },
};

function Toggle({
  checked,
  onChange,
  label,
  description,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  description?: string;
}) {
  return (
    <div className="flex items-center justify-between py-3">
      <div className="flex-1 pr-4">
        <p className="font-medium text-gray-900 dark:text-white">{label}</p>
        {description && (
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{description}</p>
        )}
      </div>
      <button
        onClick={() => onChange(!checked)}
        role="switch"
        aria-checked={checked}
        aria-label={label}
        className={`relative w-14 h-8 rounded-full transition-colors touch-manipulation ${
          checked ? 'bg-orange-500' : 'bg-gray-300 dark:bg-gray-600'
        }`}
      >
        <span
          className={`absolute top-1 w-6 h-6 bg-white rounded-full shadow transition-transform ${
            checked ? 'translate-x-7' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );
}

function SelectField({
  value,
  onChange,
  label,
  options,
}: {
  value: number;
  onChange: (value: number) => void;
  label: string;
  options: { value: number; label: string }[];
}) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="w-full flex items-center justify-between py-3 touch-manipulation"
      >
        <span className="font-medium text-gray-900 dark:text-white">{label}</span>
        <div className="flex items-center gap-2">
          <span className="text-gray-500 dark:text-gray-400">
            {options.find(o => o.value === value)?.label}
          </span>
          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>
      </button>

      {isOpen && (
        <div className="fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-black/50 animate-fadeIn"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute bottom-0 left-0 right-0 bg-white dark:bg-gray-800 rounded-t-3xl shadow-2xl animate-slideUp safe-area-inset-bottom">
            <div className="flex justify-center py-3">
              <div className="w-12 h-1.5 bg-gray-300 dark:bg-gray-600 rounded-full" />
            </div>
            <div className="px-4 pb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{label}</h3>
              <div className="space-y-2">
                {options.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => {
                      onChange(opt.value);
                      setIsOpen(false);
                    }}
                    className={`w-full py-4 px-4 rounded-xl font-medium text-left transition-colors touch-manipulation ${
                      value === opt.value
                        ? 'bg-orange-500 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default function MobileQuizSettings({ settings, onChange, onBack }: MobileQuizSettingsProps) {
  const { t } = useTranslation('mobile');
  const [activeSection, setActiveSection] = useState<Section>('timing');

  const updateSetting = <K extends keyof LiveQuizSettingsConfig>(key: K, value: LiveQuizSettingsConfig[K]) => {
    onChange({ ...settings, [key]: value });
  };

  return (
    <div className="h-screen flex flex-col bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3 safe-area-inset-top">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            aria-label="Go back"
            className="p-2 -ml-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg touch-manipulation"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <h1 className="font-semibold text-gray-900 dark:text-white">{t('quizSettings.title')}</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400">{t('quizSettings.subtitle')}</p>
          </div>
        </div>
      </header>

      {/* Section Tabs */}
      <div className="flex gap-2 px-4 py-3 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 overflow-x-auto scrollbar-hide">
        {SECTIONS.map((section) => {
          const info = sectionInfo[section];
          const colorClasses = {
            blue: activeSection === section ? 'bg-blue-500 text-white' : 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
            orange: activeSection === section ? 'bg-orange-500 text-white' : 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400',
            purple: activeSection === section ? 'bg-purple-500 text-white' : 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
            green: activeSection === section ? 'bg-green-500 text-white' : 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400',
          }[info.color];

          return (
            <button
              key={section}
              onClick={() => setActiveSection(section)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap transition-colors touch-manipulation ${colorClasses}`}
            >
              {info.icon}
              <span className="font-medium">{t(`quizSettings.sections.${section}`)}</span>
            </button>
          );
        })}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-4">
          {/* Timing Section */}
          {activeSection === 'timing' && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 space-y-1">
              <SelectField
                label={t('quizSettings.timing.defaultTime')}
                value={settings.questionTimeSeconds}
                onChange={(v) => updateSetting('questionTimeSeconds', v)}
                options={[
                  { value: 10, label: t('questionEditor.seconds', { count: 10 }) },
                  { value: 15, label: t('questionEditor.seconds', { count: 15 }) },
                  { value: 20, label: t('questionEditor.seconds', { count: 20 }) },
                  { value: 30, label: t('questionEditor.seconds', { count: 30 }) },
                  { value: 45, label: t('questionEditor.seconds', { count: 45 }) },
                  { value: 60, label: t('questionEditor.seconds', { count: 60 }) },
                ]}
              />
              <div className="border-t border-gray-100 dark:border-gray-700" />
              <Toggle
                label={t('quizSettings.timing.autoAdvance')}
                description={t('quizSettings.timing.autoAdvanceDescription')}
                checked={settings.autoAdvance}
                onChange={(v) => updateSetting('autoAdvance', v)}
              />
              {settings.autoAdvance && (
                <>
                  <div className="border-t border-gray-100 dark:border-gray-700" />
                  <SelectField
                    label={t('quizSettings.timing.delayBeforeAdvance')}
                    value={settings.autoAdvanceDelaySeconds}
                    onChange={(v) => updateSetting('autoAdvanceDelaySeconds', v)}
                    options={[
                      { value: 3, label: t('questionEditor.seconds', { count: 3 }) },
                      { value: 5, label: t('questionEditor.seconds', { count: 5 }) },
                      { value: 10, label: t('questionEditor.seconds', { count: 10 }) },
                    ]}
                  />
                </>
              )}
            </div>
          )}

          {/* Scoring Section */}
          {activeSection === 'scoring' && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 space-y-1">
              <Toggle
                label={t('quizSettings.scoring.timeBased')}
                description={t('quizSettings.scoring.timeBasedDescription')}
                checked={settings.timeBasedScoring}
                onChange={(v) => updateSetting('timeBasedScoring', v)}
              />
              <div className="border-t border-gray-100 dark:border-gray-700" />
              <Toggle
                label={t('quizSettings.scoring.streakBonus')}
                description={t('quizSettings.scoring.streakBonusDescription')}
                checked={settings.enableStreakBonus}
                onChange={(v) => updateSetting('enableStreakBonus', v)}
              />
              {settings.enableStreakBonus && (
                <>
                  <div className="border-t border-gray-100 dark:border-gray-700" />
                  <SelectField
                    label={t('quizSettings.scoring.streakThreshold')}
                    value={settings.streakThreshold}
                    onChange={(v) => updateSetting('streakThreshold', v)}
                    options={[
                      { value: 2, label: t('quizSettings.scoring.inARow', { count: 2 }) },
                      { value: 3, label: t('quizSettings.scoring.inARow', { count: 3 }) },
                      { value: 5, label: t('quizSettings.scoring.inARow', { count: 5 }) },
                    ]}
                  />
                  <div className="border-t border-gray-100 dark:border-gray-700" />
                  <SelectField
                    label={t('quizSettings.scoring.bonusMultiplier')}
                    value={Math.round(settings.streakBonusMultiplier * 10)}
                    onChange={(v) => {
                      // Use explicit mapping to avoid floating point precision issues
                      const multiplierMap: Record<number, number> = { 11: 1.1, 12: 1.2, 15: 1.5, 20: 2.0 };
                      updateSetting('streakBonusMultiplier', multiplierMap[v] ?? v / 10);
                    }}
                    options={[
                      { value: 11, label: '1.1x' },
                      { value: 12, label: '1.2x' },
                      { value: 15, label: '1.5x' },
                      { value: 20, label: '2x' },
                    ]}
                  />
                </>
              )}
            </div>
          )}

          {/* Display Section */}
          {activeSection === 'display' && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 space-y-1">
              <Toggle
                label={t('quizSettings.display.showOnDevice')}
                description={t('quizSettings.display.showOnDeviceDescription')}
                checked={settings.showQuestionOnPlayerDevice}
                onChange={(v) => updateSetting('showQuestionOnPlayerDevice', v)}
              />
              <div className="border-t border-gray-100 dark:border-gray-700" />
              <Toggle
                label={t('quizSettings.display.showLeaderboard')}
                description={t('quizSettings.display.showLeaderboardDescription')}
                checked={settings.showLeaderboardAfterQuestion}
                onChange={(v) => updateSetting('showLeaderboardAfterQuestion', v)}
              />
              {settings.showLeaderboardAfterQuestion && (
                <>
                  <div className="border-t border-gray-100 dark:border-gray-700" />
                  <SelectField
                    label={t('quizSettings.display.topPlayers')}
                    value={settings.leaderboardSize}
                    onChange={(v) => updateSetting('leaderboardSize', v)}
                    options={[
                      { value: 3, label: 'Top 3' },
                      { value: 5, label: 'Top 5' },
                      { value: 10, label: 'Top 10' },
                    ]}
                  />
                </>
              )}
              <div className="border-t border-gray-100 dark:border-gray-700" />
              <Toggle
                label={t('quizSettings.display.showDistribution')}
                description={t('quizSettings.display.showDistributionDescription')}
                checked={settings.showAnswerDistribution}
                onChange={(v) => updateSetting('showAnswerDistribution', v)}
              />
              <div className="border-t border-gray-100 dark:border-gray-700" />
              <Toggle
                label={t('quizSettings.display.revealAnswer')}
                description={t('quizSettings.display.revealAnswerDescription')}
                checked={settings.showCorrectAnswer}
                onChange={(v) => updateSetting('showCorrectAnswer', v)}
              />
            </div>
          )}

          {/* Gameplay Section */}
          {activeSection === 'gameplay' && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 space-y-1">
              <Toggle
                label={t('quizSettings.gameplay.allowLateJoin')}
                description={t('quizSettings.gameplay.allowLateJoinDescription')}
                checked={settings.allowLateJoin}
                onChange={(v) => updateSetting('allowLateJoin', v)}
              />
              <div className="border-t border-gray-100 dark:border-gray-700" />
              <Toggle
                label={t('quizSettings.gameplay.randomizeQuestions')}
                description={t('quizSettings.gameplay.randomizeQuestionsDescription')}
                checked={settings.randomizeQuestions}
                onChange={(v) => updateSetting('randomizeQuestions', v)}
              />
              <div className="border-t border-gray-100 dark:border-gray-700" />
              <Toggle
                label={t('quizSettings.gameplay.randomizeOptions')}
                description={t('quizSettings.gameplay.randomizeOptionsDescription')}
                checked={settings.randomizeOptions}
                onChange={(v) => updateSetting('randomizeOptions', v)}
              />
              <div className="border-t border-gray-100 dark:border-gray-700" />
              <Toggle
                label={t('quizSettings.gameplay.soundEffects')}
                description={t('quizSettings.gameplay.soundEffectsDescription')}
                checked={settings.enableSounds}
                onChange={(v) => updateSetting('enableSounds', v)}
              />
            </div>
          )}
        </div>
      </div>

      {/* Done Button */}
      <div className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 p-4 safe-area-inset-bottom">
        <button
          onClick={onBack}
          className="w-full py-4 bg-orange-500 text-white rounded-xl font-bold shadow-lg shadow-orange-500/30 active:scale-[0.98] transition-transform touch-manipulation"
        >
          {t('quizSettings.done')}
        </button>
      </div>
    </div>
  );
}
